import { useState, useEffect } from 'react';
import useApi from '../Hooks/useApi.js';
import {
  defaultAppearanceData,
  defaultFonts,
  defaultSettings,
  defaultTheme,
} from '../Pages/Form/Style/defaults.js';

/** Utility: convert Mongo‑style “$numberInt” fields to real numbers */
const extractNumber = (field) =>
  typeof field === 'object' && field !== null && '$numberInt' in field
    ? parseInt(field.$numberInt, 10)
    : field;

/**
 * Fetches + merges restaurant data (or falls back to defaults).
 * Additionally exposes the “event‑cards” payload from the API as `menuData`
 * so that components such as <PhotoGallery /> can consume it directly.
 */
export default function useRestaurantData() {
  const api = useApi();
  const [restaurantData, setRestaurantData] = useState(null);

  useEffect(() => {
    const fetchRestaurantData = async () => {
      try {
        const endpoint = `${window.baseDomain}api/restaurant/${window.restaurantId}`;
        const data = await api.get(endpoint, { noCache: false });

        // ───── Fallback on error / 404 / 500 ─────
        if (!data || data.status === 404 || data.status === 500) {
          setRestaurantData({
            settings: defaultSettings,
            fonts: defaultFonts,
            colors: defaultAppearanceData,
            theme: defaultTheme,
            menuData: [], // nothing to show
          });
          return;
        }

        // ───── Merge style‑related payloads with defaults ─────
        const mergedSettings = { ...defaultSettings, ...(data.settings || {}) };
        const mergedFonts    = { ...defaultFonts,    ...(data.fonts    || {}) };
        const mergedColors   = { ...defaultAppearanceData, ...(data.colors || {}) };
        const mergedTheme    = { ...defaultTheme,   ...(data.theme   || {}) };

        // ───── Parse numeric sub‑fields ─────
        const parsedTheme = {
          id:    extractNumber(mergedTheme.id),
          title: mergedTheme.title,
          color: mergedTheme.color,
          image: mergedTheme.image,
        };

        const parsedSettings = {
          ...mergedSettings,
          storedNumber: extractNumber(mergedSettings.storedNumber),
        };

        // ───── NEW: robustly unwrap “event‑cards” ─────
        // – Works whether the backend sends `event-cards.menuItems`,
        //   `eventCards.menuItems`, a flat `eventCards` array, or nothing at all.
        /* eslint‑disable no-nested-ternary */
        const rawEventCards =
          (data['event-cards'] && data['event-cards'].menuItems)
          ?? (data.eventCards && data.eventCards.menuItems)
          ?? (Array.isArray(data.eventCards) ? data.eventCards : [])
          ?? [];
        /* eslint‑enable no-nested-ternary */

        // Guarantee each card has a numeric `id` (needed for React keys)
        const parsedMenuData = rawEventCards.map((card, idx) => ({
          id: card.id ?? card._id ?? idx + 1,
          ...card,
        }));

        // ───── Lift everything into state ─────
        setRestaurantData({
          ...data,            // keep any other backend fields
          settings : parsedSettings,
          fonts    : mergedFonts,
          colors   : mergedColors,
          theme    : parsedTheme,
          menuData : parsedMenuData,
        });
      } catch (err) {
        console.error('Error fetching restaurant data:', err);
        setRestaurantData({
          settings: defaultSettings,
          fonts: defaultFonts,
          colors: defaultAppearanceData,
          theme: defaultTheme,
          menuData: [],
        });
      }
    };

    fetchRestaurantData();
  }, [api]);

  return restaurantData;
}
